import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { useAuth } from '../../Context/AuthContext';
import { PermissionCodes, permissionHas, resolveUserPermissionCodes } from '../../Utils/Permissions';
import { APPRoutes } from '../../Utils/Route';
import SearchableDropdown from '../../Components/Subscription/SearchableDropdown';
import AddressSelectorFields from '../../Components/Common/AddressSelectorFields';
import { getAllOrganizationNames } from '../../Services/OrganizationService';
import { saveStaff, getStaffById } from '../../Services/StaffService';
import CustomPhoneInput from '../../Components/Common/CustomPhoneInput';
import DotLottie from '../../Components/Common/DotLottie';

const STATUS_OPTIONS = [
  { id: 1, label: 'Active' },
  { id: 2, label: 'Inactive' },
];

const emptyForm = {
  id: null,
  organization_id: '',
  account_id: '',
  status_id: 1,
  first_name: '',
  last_name: '',
  email: '',
  title: '',
  phone_number: '',
  address_id: null,
  address: {
    street: '',
    street2: '',
    city: '',
    state: '',
    country: '',
    zipcode: '',
  },
};

const validationSchema = Yup.object().shape({
  first_name: Yup.string().trim().required('First name is required'),
  last_name: Yup.string().trim().required('Last name is required'),
  email: Yup.string().trim().email('Invalid email address').required('Email is required'),
  title: Yup.string().trim().required('Title is required'),
  status_id: Yup.number().required('Status is required'),
  address: Yup.object().shape({
    street: Yup.string().trim().required('Street Address is required'),
    street2: Yup.string().trim().nullable(),
    city: Yup.string().trim().required('City is required'),
    state: Yup.string().trim().required('State is required'),
    country: Yup.string().trim().required('Country is required'),
    zipcode: Yup.string().trim().required('ZIP Code is required'),
  }),
});

function mapApiToForm(staffData) {
  const address = staffData?.address ?? {};

  return {
    id: staffData?.id ?? null,
    organization_id: staffData?.organization_id ?? staffData?.OrganizationId ?? staffData?.organizationId ?? '',
    account_id: staffData?.account_id ?? staffData?.AccountId ?? staffData?.accountId ?? '',
    status_id: Number(staffData?.status_id ?? staffData?.StatusId ?? 1) || 1,
    first_name: staffData?.first_name ?? staffData?.FirstName ?? '',
    last_name: staffData?.last_name ?? staffData?.LastName ?? '',
    email: staffData?.email ?? staffData?.Email ?? '',
    title: staffData?.title ?? staffData?.Title ?? '',
    phone_number: staffData?.phone_number ?? staffData?.PhoneNumber ?? '',
    address_id: staffData?.address_id ?? staffData?.AddressId ?? staffData?.address?.id ?? null,
    address: {
      street: address?.street ?? address?.line1 ?? address?.Line1 ?? '',
      street2: address?.street2 ?? address?.line2 ?? address?.Line2 ?? '',
      city: address?.city ?? address?.City ?? '',
      state: address?.state ?? address?.State ?? '',
      country: address?.country ?? address?.Country ?? '',
      zipcode: address?.zipcode ?? address?.postal_code ?? address?.PostalCode ?? '',
    },
  };
}

function buildPayload(values) {
  return {
    id: values.id || null,
    organization_id: values.organization_id || null,
    account_id: values.account_id || null,
    status_id: values.status_id,
    first_name: values.first_name,
    last_name: values.last_name,
    email: values.email,
    title: values.title || null,
    phone_number: values.phone_number || null,
    address_id: values.address_id || null,
    type: values.id ? 'update' : 'create',
    address: {
      street: values.address.street,
      street2: values.address.street2,
      city: values.address.city,
      state: values.address.state,
      country: values.address.country,
      zipcode: values.address.zipcode,
    },
  };
}

export default function StaffsForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { token, permissions, decodedToken } = useAuth();

  const userPermissionCodes = React.useMemo(() => resolveUserPermissionCodes({ permissions, decodedToken }), [permissions, decodedToken]);
  const canCreateStaff = permissionHas(userPermissionCodes, PermissionCodes.STAFFS_CREATE, decodedToken);
  const canEditStaff = permissionHas(userPermissionCodes, PermissionCodes.STAFFS_EDIT, decodedToken);
  const canSave = isEdit ? canEditStaff : canCreateStaff;

  const [loading, setLoading] = useState(isEdit);
  const [organizations, setOrganizations] = useState([]);
  const [orgLoading, setOrgLoading] = useState(false);

  const formik = useFormik({
    initialValues: emptyForm,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, helpers) => {
      try {
        helpers.setSubmitting(true);
        const payload = buildPayload(values);
        const response = await saveStaff(payload, token);

        if (response?.is_success) {
          toast.success(response?.message || 'Staff saved successfully');
          const staffId = response?.data?.id || response?.data || id;
          navigate(APPRoutes.STAFFS_VIEW.replace(':id', staffId));
        } else if (response?.status_code === 500) {
          toast.error('Internal Server Error. Please contact support.');
        } else {
          toast.error(response?.message || 'Unable to save staff');
        }
      } catch (error) {
        console.error(error);
        toast.error(error?.message || 'Unable to save staff');
      } finally {
        helpers.setSubmitting(false);
      }
    },
  });

  const normalizeArray = (value) => (Array.isArray(value) ? value : []);

  const loadOrganizations = async () => {
    try {
      setOrgLoading(true);
      const response = await getAllOrganizationNames(token);
      if (response?.is_success) {
        setOrganizations(normalizeArray(response?.data?.Organizations ?? response?.data ?? response));
      } else {
        setOrganizations([]);
      }
    } catch (error) {
      console.error('Failed to load organizations:', error);
      setOrganizations([]);
    } finally {
      setOrgLoading(false);
    }
  };

  const loadStaff = async () => {
    if (!isEdit) return;

    try {
      setLoading(true);
      const response = await getStaffById(id, token);
      if (response?.is_success) {
        formik.setValues(mapApiToForm(response?.data ?? response));
      } else if (response?.status_code === 500) {
        toast.error('Internal Server Error. Please contact support.');
      } else {
        toast.error(response?.message || 'Unable to load staff');
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.message || 'Unable to load staff');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    loadOrganizations();
    if (isEdit) {
      loadStaff();
    } else {
      formik.resetForm({ values: emptyForm });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, id]);

  const renderAsterisk = () => <span className="text-danger">*</span>;

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <DotLottie />
      </div>
    );
  }

  return (
    (!isEdit && !canCreateStaff) ? (
      <div className="container-fluid py-4">
        <div className="p-4 bg-white rounded-4 shadow-sm">
          <h5 className="mb-2">Permission denied</h5>
          <p className="text-muted">You do not have permission to create staff.</p>
          <button type="button" className="btn btn-outline-primary" onClick={() => navigate(APPRoutes.STAFFS)}>
            Back to Staffs
          </button>
        </div>
      </div>
    ) : (
    <div className="container-fluid p-0">
      <div className="rounded-4 bg-white p-3 p-md-4 shadow-sm border-0">
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div className="d-flex align-items-center gap-3">
            <button
              type="button"
              className="btn btn-light border rounded-circle shadow-sm"
              onClick={() => navigate(-1)}
              style={{ width: 40, height: 40 }}
            >
              &larr;
            </button>
            <div>
              <h3 className="fw-bold mb-0 hc-text-primary">
                {isEdit ? 'Edit Staff' : 'Create Staff'}
              </h3>
              <small className="text-muted">Create or update staff member details</small>
            </div>
          </div>
        </div>

        <form onSubmit={formik.handleSubmit}>
          <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
            <h6 className="fw-bold hc-text-primary mb-3">Staff Information</h6>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label fw-semibold">
                  First Name {renderAsterisk()}
                </label>
                <input
                  name="first_name"
                  placeholder="First name"
                  className={`form-control rounded-3 shadow-sm ${formik.touched.first_name && formik.errors.first_name ? 'is-invalid' : ''}`}
                  value={formik.values.first_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <div className="invalid-feedback">{formik.touched.first_name && formik.errors.first_name ? formik.errors.first_name : ''}</div>
              </div>

              <div className="col-md-6">
                <label className="form-label fw-semibold">
                  Last Name {renderAsterisk()}
                </label>
                <input
                  name="last_name"
                  placeholder="Last name"
                  className={`form-control rounded-3 shadow-sm ${formik.touched.last_name && formik.errors.last_name ? 'is-invalid' : ''}`}
                  value={formik.values.last_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <div className="invalid-feedback">{formik.touched.last_name && formik.errors.last_name ? formik.errors.last_name : ''}</div>
              </div>
            </div>

            <div className="row g-3 mt-1">
              <div className="col-md-6">
                <label className="form-label fw-semibold">
                  Email {renderAsterisk()}
                </label>
                <input
                  name="email"
                  placeholder="name@example.com"
                  className={`form-control rounded-3 shadow-sm ${formik.touched.email && formik.errors.email ? 'is-invalid' : ''}`}
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <div className="invalid-feedback">{formik.touched.email && formik.errors.email ? formik.errors.email : ''}</div>
              </div>

              <div className="col-md-6">
                <label className="form-label fw-semibold">
                  Title {renderAsterisk()}
                </label>
                <input
                  name="title"
                  placeholder="Job title"
                  className={`form-control rounded-3 shadow-sm ${formik.touched.title && formik.errors.title ? 'is-invalid' : ''}`}
                  value={formik.values.title}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <div className="invalid-feedback">{formik.touched.title && formik.errors.title ? formik.errors.title : ''}</div>
              </div>
            </div>

            <div className="row g-3 mt-1">
              <div className="col-md-6">
                <label className="form-label fw-semibold">Phone</label>
                {/* <input
                  name="phone_number"
                  placeholder="(555) 123-4567"
                  className="form-control rounded-3 shadow-sm"
                  value={formik.values.phone_number}
                  onChange={formik.handleChange}
                /> */}

                <CustomPhoneInput
                  formik={formik}
                  name="phone_number"
                  placeholder="(555) 123-4567"
                  defaultCountry='us'
                />

              </div>

              <div className="col-md-6">
                <SearchableDropdown
                  label="Status"
                  placeholder="Select Status"
                  options={STATUS_OPTIONS.map((item) => ({ value: item.id, label: item.label }))}
                  value={formik.values.status_id}
                  onChange={(value) => formik.setFieldValue('status_id', value !== '' ? Number(value) : '')}
                  showSearch={false}
                  required
                  error={formik.touched.status_id && formik.errors.status_id ? formik.errors.status_id : ''}
                />
              </div>
            </div>

            <div className="row g-3 mt-1">
              <div className="col-md-6">
                <SearchableDropdown
                  label="Organization"
                  placeholder="Select organization"
                  options={organizations.map((org) => ({
                    value: org.id ?? org.OrganizationId ?? org.organization_id ?? org.Id ?? org.value ?? '',
                    label: org.group_office_name ?? org.OrganizationName ?? org.organization_name ?? org.name ?? org.label ?? 'Unknown Organization',
                  }))}
                  value={formik.values.organization_id}
                  onChange={(value) => formik.setFieldValue('organization_id', value || '')}
                  loading={orgLoading}
                  disabled={orgLoading}
                  required={false}
                  emptyMessage="No organizations found"
                />
              </div>

            </div>
          </div>

          <div className="p-4 rounded-4 border bg-light-subtle">
            <h6 className="fw-bold hc-text-primary mb-3">Address</h6>
            <AddressSelectorFields
              title="Staff Address"
              fieldPrefix="address"
              values={formik.values.address}
              errors={formik.errors.address || {}}
              touched={formik.touched.address || {}}
              onFieldChange={(fieldName, value) => formik.setFieldValue(fieldName, value)}
              renderAsterisk={renderAsterisk}
            />
          </div>

          <div className="text-end mt-4">
            <button
              type="submit"
              className="btn hc-bg-primary text-white px-5 py-2 rounded-3 fw-semibold shadow"
              disabled={formik.isSubmitting || !canSave}
            >
              {formik.isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  Saving...
                </>
              ) : (
                (canSave ? 'Save' : 'You do not have permission to save')
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
    )
  );
}
